import { useState } from 'react';
import {
  Button,
  ChipToggle,
  Dialog,
  Field,
  Input,
  useToast,
} from '@/components/ui';
import { useBillActions } from '@/features/billing/api';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import {
  DISCOUNT_REASONS,
  METHOD_LABEL,
  REFUND_REASONS,
  type Bill,
} from '@/features/billing/types';
import styles from '@/pages/queue/queue.module.css';

/** "Discount" (artifact #35): amount and reason; waits for the manager. */
export function DiscountDialog({
  bill: b,
  onClose,
  onError,
}: {
  bill: Bill;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const a = useBillActions(b.id);
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<string>('financial_hardship');
  const [note, setNote] = useState('');
  const sen = parseRM(amount);
  const max = b.balanceSen - b.taxSen;
  const ok = sen !== null && sen >= 1 && sen <= max;
  return (
    <Dialog
      open
      title={`Discount · ${b.number}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button
            variant="primary"
            disabled={!ok}
            loading={a.requestDiscount.isPending}
            onClick={() =>
              sen !== null &&
              a.requestDiscount
                .mutateAsync({
                  amountSen: sen,
                  reason,
                  note: note.trim() || undefined,
                })
                .then(() => {
                  toast.show({
                    tone: 'success',
                    title: 'Sent to the manager for approval',
                  });
                  onClose();
                })
                .catch(onError)
            }
          >
            Send for approval
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p>
          Patient pays now <strong>{formatRM(b.balanceSen)}</strong>
        </p>
        <Field
          label="Amount (RM)"
          error={amount && !ok ? `Up to ${formatRM(max)}` : undefined}
        >
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(ev) => setAmount(ev.target.value)}
          />
        </Field>
        <ChipToggle
          label="Reason"
          single
          options={DISCOUNT_REASONS.map((r) => ({ ...r }))}
          selected={[reason]}
          onChange={(v) => v[0] && setReason(v[0])}
        />
        <Field label="Details for the manager">
          <Input value={note} onChange={(ev) => setNote(ev.target.value)} />
        </Field>
        <p className={styles.sub}>
          Discounts apply only to what the patient pays, never to panel claims.
          Advertised free services are not allowed (MAB guideline); a discount
          is given case by case.
        </p>
      </div>
    </Dialog>
  );
}

/** "Request a refund" (artifact #33): nothing is paid out until approved. */
export function RefundDialog({
  bill: b,
  maxSen,
  onClose,
  onError,
}: {
  bill: Bill;
  maxSen: number;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const a = useBillActions(b.id);
  const toast = useToast();
  const original = [...b.payments]
    .reverse()
    .find((p) => ['card', 'e_wallet', 'duitnow_qr'].includes(p.method));
  const [amount, setAmount] = useState(senToInput(maxSen));
  const [reason, setReason] = useState<string>('service_not_given');
  const [by, setBy] = useState<'original' | 'cash'>(
    original ? 'original' : 'cash',
  );
  const [note, setNote] = useState('');
  const sen = parseRM(amount);
  const ok = sen !== null && sen >= 1 && sen <= maxSen;
  return (
    <Dialog
      open
      title={`Request refund · ${b.number}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button
            variant="primary"
            disabled={!ok}
            loading={a.requestRefund.isPending}
            onClick={() =>
              sen !== null &&
              a.requestRefund
                .mutateAsync({
                  amountSen: sen,
                  reason,
                  refundBy: by,
                  note: note.trim() || undefined,
                })
                .then(() => {
                  toast.show({
                    tone: 'success',
                    title: 'Refund waiting for the manager',
                  });
                  onClose();
                })
                .catch(onError)
            }
          >
            Send for approval
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <dl className={styles.rows}>
          <dt>Patient</dt>
          <dd>{b.patient.fullName}</dd>
          <dt>Paid</dt>
          <dd>
            {b.payments
              .map(
                (p) =>
                  `${formatRM(p.amountSen)} · ${METHOD_LABEL[p.method]} · ${new Date(p.receivedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
              )
              .join('; ')}
          </dd>
        </dl>
        <Field
          label="Amount (RM)"
          error={amount && !ok ? `Up to ${formatRM(maxSen)}` : undefined}
        >
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(ev) => setAmount(ev.target.value)}
          />
        </Field>
        <ChipToggle
          label="Reason"
          single
          options={REFUND_REASONS.map((r) => ({ ...r }))}
          selected={[reason]}
          onChange={(v) => v[0] && setReason(v[0])}
        />
        <ChipToggle
          label="Refund by"
          single
          options={[
            ...(original
              ? [
                  {
                    value: 'original' as const,
                    label:
                      original.method === 'card'
                        ? 'Back to the same card'
                        : `Back by ${METHOD_LABEL[original.method]}`,
                  },
                ]
              : []),
            { value: 'cash' as const, label: 'Cash' },
          ]}
          selected={[by]}
          onChange={(v) => v[0] && setBy(v[0])}
        />
        <Field label="Details for the manager">
          <Input
            placeholder="What happened"
            value={note}
            onChange={(ev) => setNote(ev.target.value)}
          />
        </Field>
        <p className={styles.sub}>
          The refund waits for a manager&apos;s approval. Nothing is paid out
          until then.
        </p>
      </div>
    </Dialog>
  );
}
